import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Pencil, Trash2, Search, Package, Upload, Download, FileSpreadsheet, CheckCircle2, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : window.location.origin);

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category_id: '',
    price: '',
    cost: '',
    stock: '',
    unit_type: 'unidad',
    image_url: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/products`, { withCredentials: true }),
        axios.get(`${API_URL}/api/categories`, { withCredentials: true })
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.sku.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        sku: product.sku,
        category_id: product.category_id,
        price: product.price.toString(),
        cost: product.cost.toString(),
        stock: product.stock.toString(),
        unit_type: product.unit_type,
        image_url: product.image_url || '',
        is_active: product.is_active
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        sku: '',
        category_id: categories[0]?.id || '',
        price: '',
        cost: '',
        stock: '0',
        unit_type: 'unidad',
        image_url: '',
        is_active: true
      });
    }
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        cost: parseFloat(formData.cost) || 0,
        stock: parseInt(formData.stock)
      };

      if (editingProduct) {
        await axios.put(`${API_URL}/api/products/${editingProduct.id}`, data, { withCredentials: true });
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API_URL}/api/products`, data, { withCredentials: true });
        toast.success('Producto creado');
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar producto');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;
    try {
      await axios.delete(`${API_URL}/api/products/${productId}`, { withCredentials: true });
      toast.success('Producto eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const downloadTemplate = () => {
    const csvContent = `name,sku,category_name,price,cost,stock,unit_type
Almendras 500g,ALM500,Frutos Secos,7500,4500,100,bolsa
Chocolate Bitter 1kg,CHOB1KG,Chocolates,12000,7200,50,barra
Harina Leudante 1kg,HARL1KG,Insumos Pastelería,1500,900,200,paquete`;
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'plantilla_productos.csv';
    link.click();
    toast.success('Plantilla descargada');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Solo se permiten archivos CSV');
      return;
    }

    setImportLoading(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axios.post(`${API_URL}/api/products/bulk-import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(data);
      
      if (data.success_count > 0) {
        toast.success(`${data.success_count} productos importados correctamente`);
        fetchData();
      }
      
      if (data.error_count > 0) {
        toast.warning(`${data.error_count} errores durante la importación`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar productos');
      setImportResult({ success_count: 0, error_count: 1, errors: [error.response?.data?.detail || 'Error desconocido'] });
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-stone-900 tracking-tight font-heading">Productos</h1>
          <p className="text-stone-500 mt-1">{products.length} productos registrados</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setImportModalOpen(true)}
            data-testid="import-products-btn"
            className="h-12 px-4 rounded-xl border-stone-300 hover:bg-stone-50"
          >
            <Upload className="w-5 h-5 mr-2" />
            Importar CSV
          </Button>
          <Button
            onClick={() => openModal()}
            data-testid="add-product-btn"
            className="h-12 px-6 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold active:scale-[0.98]"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuevo Producto
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
        <Input
          type="text"
          placeholder="Buscar por nombre o SKU..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-testid="products-search"
          className="pl-12 h-12 rounded-xl bg-white border-stone-200"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-semibold">Producto</TableHead>
              <TableHead className="font-semibold">SKU</TableHead>
              <TableHead className="font-semibold">Categoría</TableHead>
              <TableHead className="font-semibold text-right">Precio</TableHead>
              <TableHead className="font-semibold text-right">Stock</TableHead>
              <TableHead className="font-semibold text-center">Estado</TableHead>
              <TableHead className="font-semibold text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-stone-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                  No hay productos
                </TableCell>
              </TableRow>
            ) : (
              filteredProducts.map((product) => (
                <TableRow key={product.id} className="hover:bg-stone-50/50" data-testid={`product-row-${product.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                        {product.image_url ? (
                          <img src={product.image_url} alt="" className="w-full h-full object-cover rounded-lg" />
                        ) : (
                          <Package className="w-5 h-5 text-stone-400" />
                        )}
                      </div>
                      <span className="font-medium text-stone-900">{product.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-stone-600">{product.sku}</TableCell>
                  <TableCell className="text-stone-600">{product.category_name}</TableCell>
                  <TableCell className="text-right font-semibold text-stone-900">{formatCurrency(product.price)}</TableCell>
                  <TableCell className="text-right">
                    <span className={`font-medium ${product.stock <= 5 ? 'text-red-500' : 'text-stone-900'}`}>
                      {product.stock}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      product.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-500'
                    }`}>
                      {product.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openModal(product)}
                        data-testid={`edit-product-${product.id}`}
                        className="rounded-lg hover:bg-amber-50 hover:text-amber-600"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(product.id)}
                        data-testid={`delete-product-${product.id}`}
                        className="rounded-lg hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Product Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="product-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nombre</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="product-name-input"
                  className="h-12 rounded-xl"
                />
              </div>
              <div>
                <Label>SKU</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  required
                  data-testid="product-sku-input"
                  className="h-12 rounded-xl"
                />
              </div>
              <div>
                <Label>Categoría</Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="product-category-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Precio</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  data-testid="product-price-input"
                  className="h-12 rounded-xl"
                />
              </div>
              <div>
                <Label>Costo</Label>
                <Input
                  type="number"
                  value={formData.cost}
                  onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                  data-testid="product-cost-input"
                  className="h-12 rounded-xl"
                />
              </div>
              <div>
                <Label>Stock</Label>
                <Input
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  required
                  data-testid="product-stock-input"
                  className="h-12 rounded-xl"
                />
              </div>
              <div>
                <Label>Unidad</Label>
                <Select
                  value={formData.unit_type}
                  onValueChange={(value) => setFormData({ ...formData, unit_type: value })}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="product-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unidad">Unidad</SelectItem>
                    <SelectItem value="bolsa">Bolsa</SelectItem>
                    <SelectItem value="paquete">Paquete</SelectItem>
                    <SelectItem value="caja">Caja</SelectItem>
                    <SelectItem value="frasco">Frasco</SelectItem>
                    <SelectItem value="barra">Barra</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="rounded-xl">
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-product-btn" className="rounded-xl bg-amber-600 hover:bg-amber-700">
                {editingProduct ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="import-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <FileSpreadsheet className="w-6 h-6 text-amber-600" />
              Importar Productos desde CSV
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Instructions */}
            <div className="bg-stone-50 rounded-xl p-4">
              <h4 className="font-semibold text-stone-900 mb-2">Formato del archivo CSV</h4>
              <p className="text-sm text-stone-600 mb-3">
                El archivo debe tener las siguientes columnas:
              </p>
              <code className="text-xs bg-stone-200 px-2 py-1 rounded block overflow-x-auto">
                name,sku,category_name,price,cost,stock,unit_type
              </code>
              <p className="text-xs text-stone-500 mt-2">
                * Si el SKU ya existe, se actualizará el producto existente
              </p>
              <p className="text-xs text-stone-500">
                * Si la categoría no existe, se creará automáticamente
              </p>
            </div>

            {/* Download Template */}
            <Button
              variant="outline"
              onClick={downloadTemplate}
              className="w-full h-12 rounded-xl"
              data-testid="download-template-btn"
            >
              <Download className="w-5 h-5 mr-2" />
              Descargar Plantilla de Ejemplo
            </Button>

            {/* File Upload */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="csv-file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={importLoading}
                className="w-full h-14 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold"
                data-testid="select-file-btn"
              >
                {importLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Importando...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Seleccionar Archivo CSV
                  </>
                )}
              </Button>
            </div>

            {/* Import Result */}
            {importResult && (
              <div className={`rounded-xl p-4 ${importResult.error_count === 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                <div className="flex items-center gap-3 mb-3">
                  {importResult.error_count === 0 ? (
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  ) : (
                    <XCircle className="w-6 h-6 text-amber-600" />
                  )}
                  <div>
                    <p className="font-semibold text-stone-900">Resultado de importación</p>
                    <p className="text-sm text-stone-600">
                      {importResult.success_count} importados, {importResult.error_count} errores
                    </p>
                  </div>
                </div>
                
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-3 max-h-32 overflow-y-auto">
                    <p className="text-sm font-medium text-stone-700 mb-1">Errores:</p>
                    <ul className="text-xs text-stone-600 space-y-1">
                      {importResult.errors.map((err, i) => (
                        <li key={i} className="bg-white/50 px-2 py-1 rounded">{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
